import { CASCADE_ENUM } from '../../../../services/enum.service';
import { copyIcon, deleteIcon, getTechnologyIconName, pencilIcon } from '../../../../styles';
import {
  ActionCellRenderer,
  ActionTypes,
} from '../../../../utils/custom-renderer/action-cell-renderer';
import {
  HyperlinkStaticCellRenderer,
  IGridHyperlink,
} from '../../../../utils/custom-renderer/hyperlink-cell-renderer';
import {
  getPriceRange,
  getStatusTranslate,
  getFormattedNumber,
  getFormattedDate,
  getStatusColour,
  getFillColorFromContractType,
  getPriceValue,
  getCreditProfileValue,
  DEFAULT_REC_SETTLEMENT_POINT,
} from '../../../../utils/formatting';
import {
  headerWithTooltipColParams,
  filterEqualNumeric,
  getGroupColumnSameObject,
  CUSTOM_HEADER_TOOLTIPS,
  compareRFQValues,
  compareSettlementPointValues,
} from '../../../../utils/grid';
import { BUYER_DASHBOARD_GRID_TABS } from '../my-portfolio/my-portfolio.template';
import {
  ICONS,
  CONTRACT_TYPE,
  PROFILE_RIGHTS,
  PRODUCT_TYPE,
  VPPA_RFQ_STATUS,
  ICON_COLORS,
  TECHNOLOGY,
} from '../../../../utils';
import {
  filterGreaterOrEqualNumeric,
  filterLessOrEqualNumeric,
  filterNumericRange,
} from '../../../../utils/grid';
import { ALL_RFQ_TABS, AllRfq } from './all-rfq';

export enum COLUMN_FIELDS {
  TICKER = 'TICKER',
  RFQ_OBJECT = 'RFQ_OBJECT',
  RFQ_ID = 'RFQ_ID_GROUP',
  UNIQUE_ID = 'UNIQUE_ID',
  TECHNOLOGY = 'TECHNOLOGY',
  PROJECT = 'NAME',
  ISO = 'ISO',
  SETTLEMENT_POINT = 'SETTLEMENT_POINT',
  TENOR_START_DATE = 'TENOR_START_DATE',
  VOLUME = 'VOLUME',
  PRICE = 'PRICE',
  FIXED_PRICE_LOWER_BOUND = 'FIXED_PRICE_LOWER_BOUND',
  FIXED_PRICE_UPPER_BOUND = 'FIXED_PRICE_UPPER_BOUND',
  TENOR_MONTHS = 'TENOR_MONTHS',
  LME = 'LME',
  ACTIONS = 'ACTIONS',
  STATUS = 'RFQ_STATUS',
  MODIFIED_AT = 'MODIFIED_AT',
  RFQ_REFERENCE = 'RFQ_REFERENCE',
  CREDIT_PROFILE = 'CREDIT_PROFILE',
  IMPLIED_CARBON_PRICE = 'IMPLIED_CARBON_PRICE',
  APPLICABLE_REC_PROGRAM = 'APPLICABLE_REC_PROGRAM',
  CONTRACT_TYPE = 'CONTRACT_TYPE',
}

function getNestedRowStyle(params: any) {
  if (params.data?.NEGOTIATION_ID) {
    return { 'background-color': '#191919' };
  }
  return '';
}

function getCellStyle(params: any) {
  const response = {};
  if (params.data?.NEGOTIATION_ID) {
    return { 'background-color': '#191919' };
  }

  if (params.data?.NAME) {
    response['color'] = '#7acc79';
  }

  if (
    getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).RFQ_STATUS ||
    params.data?.STATUS
  ) {
    const status = params.data?.STATUS
      ? params.data?.STATUS
      : getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).RFQ_STATUS;
    response['color'] = getStatusColour(status);
  }

  return response;
}

function getTickerValueForRFQ(data: any) {
  const iconList: Array<{ icon: ICONS; width; height; fill? }> = [];

  if (data.CONTRACT_TYPE) {
    if (data.TECHNOLOGY === TECHNOLOGY.BOTH) {
      iconList.push({
        icon: getTechnologyIconName(TECHNOLOGY.WIND),
        width: '12px',
        height: '13px',
        fill: getFillColorFromContractType(data.CONTRACT_TYPE),
      });
      iconList.push({
        icon: getTechnologyIconName(TECHNOLOGY.SOLAR),
        width: '12px',
        height: '13px',
        fill: getFillColorFromContractType(data.CONTRACT_TYPE),
      });
    } else {
      iconList.push({
        icon: getTechnologyIconName(data.TECHNOLOGY),
        width: '12px',
        height: '13px',
        fill: getFillColorFromContractType(data.CONTRACT_TYPE),
      });
    }
  }

  if (data.CONTRACT_TYPE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED) {
    iconList.push({
      icon: ICONS.AUTO_EXECUTED_ICON,
      width: '16px',
      height: '16px',
      fill: ICON_COLORS.AUTO_EXECUTED_STATUS,
    });
  }

  return JSON.stringify({
    NAME: data.TICKER,
    ICON_LIST: iconList,
  });
}

export function RFQ_COLUMNS(context: AllRfq) {
  return [
    {
      headerName: '',
      cellRenderer: 'agGroupCellRenderer',
      showRowGroup: true,
      suppressMenu: true,
      lockPosition: 'left',
      minWidth: 60,
      maxWidth: 60,
      resizable: false,
      sortable: true,
      cellStyle: (params) => getNestedRowStyle(params),
    },
    {
      field: COLUMN_FIELDS.TICKER,
      headerName: 'Ticker',
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      cellStyle: (params) => getNestedRowStyle(params),
      valueGetter: (params) => {
        if (params.data) {
          // For the ungrouped rows of Active/Inactive RFQs
          return getTickerValueForRFQ(params.data);
        } else {
          //For the grouped row of trading activity
          const linkedRfqData = getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT);
          return getTickerValueForRFQ(linkedRfqData);
        }
      },
      cellRenderer: HyperlinkStaticCellRenderer,
      cellRendererParams: (params) => {
        if (!params.value && !params.data) return;

        const obj = JSON.parse(params.value);
        const hyperlinkParams: IGridHyperlink = {
          value: obj?.NAME,
          id: '',
          vppaId: null,
          iconList: obj?.ICON_LIST,
        };

        return hyperlinkParams;
      },
      comparator: (valueA, valueB, nodeA, nodeB) =>
        compareRFQValues(valueA, valueB, nodeA, nodeB, 'TICKER', 'TICKER'),
      ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.ICON_LEGEND),
    },
    {
      field: COLUMN_FIELDS.RFQ_ID,
      headerName: '',
      hide: true,
      rowGroup: true,
      suppressMenu: true,
      suppressColumnsToolPanel: true,
      valueFormatter: (params) => {
        if (params.node && params.node.allLeafChildren?.length) {
          return params.node.allLeafChildren.length;
        }
        return '';
      },
    },
    {
      field: COLUMN_FIELDS.UNIQUE_ID,
      headerName: 'Unique ID',
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      filterValueGetter: (params) => {
        return params.data.RFQ_OBJECT ? params.data.RFQ_OBJECT.UNIQUE_ID : params.data.UNIQUE_ID;
      },
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      valueFormatter: (params) =>
        params.data
          ? params.data.UNIQUE_ID
          : getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).UNIQUE_ID,
      cellStyle: (params) => getNestedRowStyle(params),
      comparator: (valueA, valueB, nodeA, nodeB) =>
        compareRFQValues(valueA, valueB, nodeA, nodeB, 'UNIQUE_ID', 'UNIQUE_ID'),
    },
    {
      field: COLUMN_FIELDS.RFQ_REFERENCE,
      headerName: 'Internal Reference',
      filter: 'agTextColumnFilter',
      lockPinned: true,
      filterValueGetter: (params) => {
        return params.data.RFQ_OBJECT
          ? params.data.RFQ_OBJECT.RFQ_REFERENCE
          : params.data.RFQ_REFERENCE;
      },
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      valueFormatter: (params) =>
        params.data
          ? params.data.RFQ_REFERENCE
          : getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).RFQ_REFERENCE,
      cellStyle: (params) => getNestedRowStyle(params),
      comparator: (valueA, valueB, nodeA, nodeB) =>
        compareRFQValues(valueA, valueB, nodeA, nodeB, 'RFQ_REFERENCE', 'RFQ_REFERENCE'),
    },
    {
      field: COLUMN_FIELDS.TECHNOLOGY,
      headerName: 'Technology',
      sortable: true,
      filter: 'agSetColumnFilter',
      lockPinned: true,
      filterValueGetter: (params) => {
        return params.data.RFQ_OBJECT ? params.data.RFQ_OBJECT.TECHNOLOGY : params.data.TECHNOLOGY;
      },
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      cellStyle: (params) => getNestedRowStyle(params),
      valueFormatter: (params) => {
        if (params.data) {
          return context.enumService.getEnumTranslation(
            CASCADE_ENUM.TECHNOLOGY,
            params.data.TECHNOLOGY,
          );
        } else {
          return context.enumService.getEnumTranslation(
            CASCADE_ENUM.TECHNOLOGY,
            getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).TECHNOLOGY,
          );
        }
      },
      comparator: (valueA, valueB, nodeA, nodeB) =>
        compareRFQValues(valueA, valueB, nodeA, nodeB, 'TECHNOLOGY', 'TECHNOLOGY'),
    },
    {
      field: COLUMN_FIELDS.PROJECT,
      headerName: 'Project',
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      filterValueGetter: (params) => {
        return params.data.RFQ_OBJECT ? params.data.RFQ_OBJECT.PROJECT : params.data.PROJECT;
      },
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      cellStyle: (params) => getCellStyle(params),
      valueFormatter: (params) =>
        params.data
          ? params.data.NAME
          : getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).NAME,
      cellRenderer: HyperlinkStaticCellRenderer,
      cellRendererParams: (params) => {
        const obj = params.data
          ? params.data
          : getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT);
        if (!obj.NAME) {
          const hyperlinkParams: IGridHyperlink = {
            value: null,
            id: null,
            vppaId: null,
            iconList: [],
          };
          return hyperlinkParams;
        }

        const hyperlinkParams: IGridHyperlink = {
          value: obj.NAME,
          id: obj.PROJECT_ID,
          vppaId: null,
          iconList: obj.ICON_LIST,
        };
        return hyperlinkParams;
      },
      comparator: (valueA, valueB, nodeA, nodeB) =>
        compareRFQValues(valueA, valueB, nodeA, nodeB, 'NAME', 'NAME'),
    },
    {
      field: COLUMN_FIELDS.ISO,
      headerName: 'ISO',
      sortable: true,
      filter: 'agSetColumnFilter',
      lockPinned: true,
      filterValueGetter: (params) => {
        return params.data.RFQ_OBJECT ? params.data.RFQ_OBJECT.ISO : params.data.ISO;
      },
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      valueFormatter: (params) =>
        params.data
          ? params.data.ISO
          : getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).ISO,
      cellStyle: (params) => getNestedRowStyle(params),
      comparator: (valueA, valueB, nodeA, nodeB) =>
        compareRFQValues(valueA, valueB, nodeA, nodeB, 'ISO', 'ISO'),
    },
    {
      field: COLUMN_FIELDS.SETTLEMENT_POINT,
      headerName: 'Settlement Point',
      sortable: true,
      filter: 'agSetColumnFilter',
      lockPinned: true,
      filterValueGetter: (params) => {
        if (params.data.RFQ_OBJECT) {
          return params.data.RFQ_OBJECT.CONTRACT_TYPE === CONTRACT_TYPE.REC
            ? DEFAULT_REC_SETTLEMENT_POINT
            : params.data.RFQ_OBJECT.SETTLEMENT_POINT;
        } else {
          return params.data.CONTRACT_TYPE === CONTRACT_TYPE.REC
            ? DEFAULT_REC_SETTLEMENT_POINT
            : params.data.SETTLEMENT_POINT;
        }
      },
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      cellStyle: (params) => getNestedRowStyle(params),
      valueFormatter: (params) => {
        if (params.data) {
          return params.data.CONTRACT_TYPE === CONTRACT_TYPE.REC
            ? DEFAULT_REC_SETTLEMENT_POINT
            : params.data.SETTLEMENT_POINT;
        } else {
          let rfqObject = getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT);
          return rfqObject.CONTRACT_TYPE === CONTRACT_TYPE.REC
            ? DEFAULT_REC_SETTLEMENT_POINT
            : rfqObject.SETTLEMENT_POINT;
        }
      },
      comparator: (valueA, valueB, nodeA, nodeB) =>
        compareSettlementPointValues(nodeA, nodeB, 'CONTRACT_TYPE', 'SETTLEMENT_POINT'),
    },
    {
      field: COLUMN_FIELDS.VOLUME,
      headerName: 'Volume',
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      filterValueGetter: (params) => {
        return params.data.RFQ_OBJECT ? params.data.RFQ_OBJECT.VOLUME : params.data.VOLUME;
      },
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      cellStyle: (params) => getNestedRowStyle(params),
      valueFormatter: (params) =>
        params.data
          ? getFormattedNumber(params.data.VOLUME)
          : getFormattedNumber(
              getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).REQUESTED_VOLUME,
            ),
      comparator: (valueA, valueB, nodeA, nodeB) =>
        compareRFQValues(valueA, valueB, nodeA, nodeB, 'REQUESTED_VOLUME', 'VOLUME'),
    },
    {
      field: COLUMN_FIELDS.PRICE,
      headerValueGetter: (params) => {
        let header = '';
        if (context.activeTabId === BUYER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_TAB) {
          header = 'Fixed Price';
        } else {
          header = 'Fixed Price Range';
        }
        return header;
      },
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      filterParams: {
        filterOptions: [
          {
            displayKey: 'priceEqual',
            displayName: 'Equals',
            predicate: ([fv1], cellValue) => filterEqualNumeric(fv1, cellValue),
            numberOfInputs: 1,
          },
          {
            displayKey: 'lessThanOrEqual',
            displayName: '<= Upper Bound',
            predicate: ([fv1], cellValue) => filterLessOrEqualNumeric(fv1, cellValue),
            numberOfInputs: 1,
          },
          {
            displayKey: 'greaterThanOrEqual',
            displayName: '>= Lower Bound',
            predicate: ([fv1], cellValue) => filterGreaterOrEqualNumeric(fv1, cellValue),
            numberOfInputs: 1,
          },
          {
            displayKey: 'inRange',
            displayName: 'In range',
            predicate: ([fv1, fv2], cellValue) => filterNumericRange([fv1, fv2], cellValue),
            numberOfInputs: 2,
          },
        ],
        suppressAndOrCondition: true,
      },
      valueGetter: (params) => {
        if (params.data) {
          const hasRfqObject = params.data.RFQ_OBJECT;
          if (
            context.activeTabId === BUYER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_TAB &&
            hasRfqObject
          ) {
            if (params.data.RFQ_OBJECT.PRODUCT_TYPE === PRODUCT_TYPE.RECS) {
              return [params.data.RFQ_OBJECT.FIXED_PRICE, params.data.RFQ_OBJECT.FIXED_PRICE];
            }
            const fixedPrice = [
              (params.data.RFQ_OBJECT.FIXED_PRICE_LOWER_BOUND +
                params.data.RFQ_OBJECT.FIXED_PRICE_UPPER_BOUND) /
                2,
              (params.data.RFQ_OBJECT.FIXED_PRICE_LOWER_BOUND +
                params.data.RFQ_OBJECT.FIXED_PRICE_UPPER_BOUND) /
                2,
            ];
            return fixedPrice;
          }
          if (params.data.PRICE && !hasRfqObject) {
            const fixedPrice = [params.data.PRICE, params.data.PRICE];
            return fixedPrice;
          } else if (
            params.data.RFQ_OBJECT &&
            params.data.RFQ_OBJECT.FIXED_PRICE_LOWER_BOUND &&
            params.data.RFQ_OBJECT.FIXED_PRICE_UPPER_BOUND
          ) {
            const rfqObjectPrices = [
              params.data.RFQ_OBJECT.FIXED_PRICE_LOWER_BOUND,
              params.data.RFQ_OBJECT.FIXED_PRICE_UPPER_BOUND,
            ];
            return rfqObjectPrices;
          } else {
            const fixedPriceBounds = [
              params.data.FIXED_PRICE_LOWER_BOUND,
              params.data.FIXED_PRICE_UPPER_BOUND,
            ];
            return fixedPriceBounds;
          }
        }
      },
      comparator: (valueA, valueB, nodeA, nodeB) => {
        let valA: number;
        let valB: number;
        if (context.activeTabId === BUYER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_TAB) {
          if (nodeA.allLeafChildren[0].data.RFQ_OBJECT.PRODUCT_TYPE === PRODUCT_TYPE.RECS) {
            valA = valueA ? valueA[0] : nodeA.allLeafChildren[0].data.RFQ_OBJECT.FIXED_PRICE;
            valB = valueB ? valueB[0] : nodeB.allLeafChildren[0].data.RFQ_OBJECT.FIXED_PRICE;
          } else {
            valA = valueA
              ? valueA[0]
              : nodeA.allLeafChildren[0].data.RFQ_OBJECT.FIXED_PRICE_LOWER_BOUND +
                nodeA.allLeafChildren[0].data.RFQ_OBJECT.FIXED_PRICE_UPPER_BOUND / 2;
            valB = valueB
              ? valueB[0]
              : nodeB.allLeafChildren[0].data.RFQ_OBJECT.FIXED_PRICE_LOWER_BOUND +
                nodeB.allLeafChildren[0].data.RFQ_OBJECT.FIXED_PRICE_UPPER_BOUND / 2;
          }
        } else {
          valA = valueA
            ? valueA[0]
            : nodeA.allLeafChildren[0].data.RFQ_OBJECT.FIXED_PRICE_LOWER_BOUND;
          valB = valueB
            ? valueB[0]
            : nodeB.allLeafChildren[0].data.RFQ_OBJECT.FIXED_PRICE_LOWER_BOUND;
        }
        if (valA && valB) {
          if (valA < valB) {
            return -1;
          } else if (valA > valB) {
            return 1;
          } else {
            return 0;
          }
        }
      },
      cellStyle: (params) => getNestedRowStyle(params),
      valueFormatter: (params) => {
        if (context.activeTabId === BUYER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_TAB) {
          if (params.data) {
            if (params.data.PRICE) {
              return getPriceValue(params.data.PRICE, '', '$');
            } else {
              if (params.data.PRODUCT_TYPE === PRODUCT_TYPE.RECS) {
                return getPriceValue(params.data.FIXED_PRICE, '', '$');
              }
              return getPriceValue(
                (params.data.FIXED_PRICE_LOWER_BOUND + params.data.FIXED_PRICE_UPPER_BOUND) / 2,
                '',
                '$',
              );
            }
          } else {
            if (
              getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).PRODUCT_TYPE ===
              PRODUCT_TYPE.RECS
            ) {
              return getPriceValue(
                getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).FIXED_PRICE,
                '',
                '$',
              );
            }
            return getPriceValue(
              (getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).FIXED_PRICE_LOWER_BOUND +
                getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT)
                  .FIXED_PRICE_UPPER_BOUND) /
                2,
              '',
              '$',
            );
          }
        } else {
          if (params.data) {
            if (params.data.PRICE) {
              return getPriceValue(params.data.PRICE, '', '$');
            } else {
              return getPriceRange(
                getPriceValue(params.data.FIXED_PRICE_LOWER_BOUND),
                getPriceValue(params.data.FIXED_PRICE_UPPER_BOUND),
              );
            }
          } else {
            return getPriceRange(
              getPriceValue(
                getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).FIXED_PRICE_LOWER_BOUND,
              ),
              getPriceValue(
                getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).FIXED_PRICE_UPPER_BOUND,
              ),
            );
          }
        }
      },
    },
    {
      field: COLUMN_FIELDS.TENOR_START_DATE,
      headerName: 'Start Date',
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      filterValueGetter: (params) => {
        return params.data.RFQ_OBJECT
          ? getFormattedDate(params.data.RFQ_OBJECT.TENOR_START_DATE)
          : getFormattedDate(params.data.TENOR_START_DATE);
      },
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      valueFormatter: (params) =>
        params.data
          ? getFormattedDate(params.data.TENOR_START_DATE)
          : getFormattedDate(
              getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).TENOR_START_DATE,
            ),
      cellStyle: (params) => getNestedRowStyle(params),
      comparator: (valueA, valueB, nodeA, nodeB) =>
        compareRFQValues(valueA, valueB, nodeA, nodeB, 'TENOR_START_DATE', 'TENOR_START_DATE'),
    },
    {
      field: COLUMN_FIELDS.TENOR_MONTHS,
      headerName: 'Tenor (mo)',
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      filterValueGetter: (params) => {
        return params.data.RFQ_OBJECT
          ? params.data.RFQ_OBJECT.TENOR_MONTHS
          : params.data.TENOR_MONTHS;
      },
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      valueFormatter: (params) =>
        params.data
          ? params.data.TENOR_MONTHS
          : getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).TENOR_MONTHS,
      cellStyle: (params) => getNestedRowStyle(params),
      comparator: (valueA, valueB, nodeA, nodeB) =>
        compareRFQValues(valueA, valueB, nodeA, nodeB, 'TENOR_MONTHS', 'TENOR_MONTHS'),
    },
    {
      field: COLUMN_FIELDS.CREDIT_PROFILE,
      headerName: 'Credit Profile ($/MW)',
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      minWidth: '80',
      cellStyle: (params) => getNestedRowStyle(params),
      valueFormatter: (params) => {
        if (params.data) {
          let creditProfile = params.data.VPPA_CREDIT_PROFILE
            ? params.data.VPPA_CREDIT_PROFILE
            : params.data.RFQ_CREDIT_PROFILE;
          return getCreditProfileValue(creditProfile, params.data.CREDIT_PROFILE);
        } else {
          let groupCreditProfile = getGroupColumnSameObject(
            params,
            COLUMN_FIELDS.RFQ_OBJECT,
          ).RFQ_CREDIT_PROFILE;
          let groupCollateral = getGroupColumnSameObject(
            params,
            COLUMN_FIELDS.RFQ_OBJECT,
          ).BUYER_POST_COD_COLLATERAL;
          return getCreditProfileValue(groupCreditProfile, groupCollateral);
        }
      },
      comparator: (valueA, valueB, nodeA, nodeB) =>
        compareRFQValues(
          valueA,
          valueB,
          nodeA,
          nodeB,
          'BUYER_POST_COD_COLLATERAL',
          'CREDIT_PROFILE',
        ),
      ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.CREDIT_PROFILE),
    },
    {
      field: COLUMN_FIELDS.IMPLIED_CARBON_PRICE,
      headerValueGetter: (params) => {
        let header = '';
        if (context.activeTabId === BUYER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_TAB) {
          header = 'Implied Carbon Price';
        } else {
          header = 'Implied Carbon Price Range Desired';
        }
        return header;
      },
      sortable: true,
      filter: 'agTextColumnFilter',
      lockPinned: true,
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      cellStyle: (params) => getNestedRowStyle(params),
      valueFormatter: (params) => {
        if (params.data) {
          if (params.data.IMPLIED_CARBON_PRICE) {
            return getPriceValue(params.data.IMPLIED_CARBON_PRICE, '', '$');
          } else {
            return getPriceRange(
              params.data.IMPLIED_CARBON_PRICE_MIN,
              params.data.IMPLIED_CARBON_PRICE_MAX,
            );
          }
        } else {
          return getPriceRange(
            getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).IMPLIED_CARBON_PRICE_MIN,
            getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).IMPLIED_CARBON_PRICE_MAX,
          );
        }
      },
      comparator: (valueA, valueB, nodeA, nodeB) =>
        compareRFQValues(
          valueA,
          valueB,
          nodeA,
          nodeB,
          'IMPLIED_CARBON_PRICE_MIN',
          'IMPLIED_CARBON_PRICE_MIN',
        ),
    },
    {
      field: COLUMN_FIELDS.APPLICABLE_REC_PROGRAM,
      headerName: 'Applicable REC Program',
      sortable: true,
      filter: 'agSetColumnFilter',
      lockPinned: true,
      filterValueGetter: (params) => {
        let applicableRecProgram = params.data.RFQ_OBJECT
          ? params.data.RFQ_OBJECT.RFQ_APPLICABLE_REC_PROGRAM
          : params.data.RFQ_APPLICABLE_REC_PROGRAM;
        return context.enumService.getEnumTranslation(
          CASCADE_ENUM.RFQ_APPLICABLE_REC_PROGRAM,
          applicableRecProgram,
        );
      },
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      cellStyle: (params) => getNestedRowStyle(params),
      valueFormatter: (params) => {
        if (params.data) {
          return context.enumService.getEnumTranslation(
            CASCADE_ENUM.RFQ_APPLICABLE_REC_PROGRAM,
            params.data.RFQ_APPLICABLE_REC_PROGRAM,
          );
        } else {
          return context.enumService.getEnumTranslation(
            CASCADE_ENUM.RFQ_APPLICABLE_REC_PROGRAM,
            getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).RFQ_APPLICABLE_REC_PROGRAM,
          );
        }
      },
      comparator: (valueA, valueB, nodeA, nodeB) =>
        compareRFQValues(
          valueA,
          valueB,
          nodeA,
          nodeB,
          'RFQ_APPLICABLE_REC_PROGRAM',
          'RFQ_APPLICABLE_REC_PROGRAM',
        ),
    },
    {
      field: COLUMN_FIELDS.CONTRACT_TYPE,
      headerName: 'Contract Type',
      sortable: true,
      sort: 'asc', // Set sorting order to ascending
      filter: 'agSetColumnFilter',
      lockPinned: true,
      filterValueGetter: (params) => {
        let contractType = params.data.RFQ_OBJECT
          ? params.data.RFQ_OBJECT.CONTRACT_TYPE
          : params.data.CONTRACT_TYPE;
        return context.enumService.getEnumTranslation(CASCADE_ENUM.CONTRACT_TYPE, contractType);
      },
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      cellStyle: (params) => getNestedRowStyle(params),
      valueFormatter: (params) => {
        if (params.data) {
          return context.enumService.getEnumTranslation(
            CASCADE_ENUM.CONTRACT_TYPE,
            params.data.CONTRACT_TYPE,
          );
        } else {
          return context.enumService.getEnumTranslation(
            CASCADE_ENUM.CONTRACT_TYPE,
            getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).CONTRACT_TYPE,
          );
        }
      },
      comparator: (valueA, valueB, nodeA, nodeB) =>
        compareRFQValues(valueA, valueB, nodeA, nodeB, 'CONTRACT_TYPE', 'CONTRACT_TYPE'),
    },
    {
      field: COLUMN_FIELDS.STATUS,
      headerName: 'Status',
      sortable: true,
      filter: 'agSetColumnFilter',
      lockPinned: true,
      filterValueGetter: (params) => {
        let status = params.data.RFQ_OBJECT
          ? params.data.RFQ_OBJECT.RFQ_STATUS
          : params.data.STATUS;
        return getStatusTranslate(status);
      },
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      cellStyle: (params) => getCellStyle(params),
      valueFormatter: (params) => {
        if (params.data) {
          return getStatusTranslate(params.data.STATUS);
        } else {
          return getStatusTranslate(
            getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).RFQ_STATUS,
          );
        }
      },
      comparator: (valueA, valueB, nodeA, nodeB) =>
        compareRFQValues(valueA, valueB, nodeA, nodeB, 'RFQ_STATUS', 'STATUS'),
    },
    {
      field: COLUMN_FIELDS.MODIFIED_AT,
      headerName: 'Updated',
      lockPinned: true,
      menuTabs: ['generalMenuTab'],
      sortable: true,
      valueFormatter: (params) =>
        params.data
          ? getFormattedDate(params.data.MODIFIED_AT)
          : getFormattedDate(
              getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT).MODIFIED_AT,
            ),
      cellStyle: (params) => getNestedRowStyle(params),
      sort: 'desc',
      comparator: (valueA, valueB, nodeA, nodeB) => {
        let valA = valueA;
        let valB = valueB;
        if (!nodeA.data) {
          let tempA = 0;
          nodeA.allLeafChildren.forEach((child) => {
            if (tempA <= child.data.RFQ_OBJECT.MODIFIED_AT || tempA == undefined) {
              tempA = child.data.RFQ_OBJECT.MODIFIED_AT;
            }
            if (tempA <= child.data.NEGOTIATION_MODIFIED_AT) {
              tempA = child.data.NEGOTIATION_MODIFIED_AT;
            }
            if (tempA <= child.data.MODIFIED_AT) {
              tempA = child.data.MODIFIED_AT;
            }
          });
          if (valA < tempA || valA == undefined) {
            valA = tempA;
          }
        } else {
          valA = nodeA.data.MODIFIED_AT;
        }

        if (!nodeB.data) {
          let tempB = 0;
          nodeB.allLeafChildren.forEach((child) => {
            if (tempB <= child.data.RFQ_OBJECT.MODIFIED_AT || tempB == undefined) {
              tempB = child.data.RFQ_OBJECT.MODIFIED_AT;
            }
            if (tempB <= child.data.NEGOTIATION_MODIFIED_AT) {
              tempB = child.data.NEGOTIATION_MODIFIED_AT;
            }
            if (tempB <= child.data.MODIFIED_AT) {
              tempB = child.data.MODIFIED_AT;
            }
          });
          if (valB < tempB || valB == undefined) {
            valB = tempB;
          }
        }

        if (valA == valB) return 0;
        return valA > valB ? 1 : -1;
      },
    },
    {
      field: COLUMN_FIELDS.ACTIONS,
      headerName: 'Actions',
      suppressMenu: true,
      maxWidth: 205,
      pinned: 'right',
      lockPinned: true,
      suppressColumnsToolPanel: true,
      cellStyle: {
        'padding-right': '7px',
        'box-shadow': '-5px 0px 15px 0px rgba(0, 0, 0, 0.2)',
      },
      cellRenderer: ActionCellRenderer,
      cellRendererParams: (params) => {
        const hasPermission: boolean = context.userService
          .getFilteredPermissions()
          .includes(PROFILE_RIGHTS.AUTO_EXECUTE);

        if (
          params.data &&
          (params.data.STATUS === VPPA_RFQ_STATUS.DELETED ||
            params.data.RFQ_STATUS === VPPA_RFQ_STATUS.DELETED)
        ) {
          const actions = [];
          return {
            actions,
            enabled: false,
          };
        } else if (
          ((params.data &&
            !params.data.RFQ_ID_GROUP &&
            (params.data.CONTRACT_TYPE !== CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED ||
              (params.data.CONTRACT_TYPE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED &&
                hasPermission))) ||
            !params.data) &&
          context.activeTabId !== BUYER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_TAB
        ) {
          const copyBtn = {
            name: copyIcon(),
            callback: (detail) => {
              if (!detail) {
                context.$emit(
                  'copyRfq',
                  getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT),
                );
              } else {
                context.$emit('copyRfq', detail);
              }
            },
            type: ActionTypes.EDIT,
            actionName: 'copy-rfq',
            tooltip: 'Copy',
          };
          const editBtn = {
            name: pencilIcon(),
            callback: (detail) => {
              if (!detail) {
                context.$emit(
                  'editRfq',
                  getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT),
                );
              } else {
                context.$emit('editRfq', detail);
              }
            },
            type: ActionTypes.EDIT,
            actionName: 'edit-rfq',
            tooltip: 'Edit',
          };
          const deleteBtn = {
            name: deleteIcon(),
            callback: (detail) => {
              if (!detail) {
                context.$emit(
                  'deleteRfq',
                  getGroupColumnSameObject(params, COLUMN_FIELDS.RFQ_OBJECT),
                );
              } else {
                context.$emit('deleteRfq', detail);
              }
            },
            type: ActionTypes.DELETE,
            actionName: 'delete-rfq',
            tooltip: 'Delete',
          };

          const actions = [copyBtn, editBtn, deleteBtn];
          return {
            actions,
            enabled: true,
          };
        } else if (
          params.data &&
          params.data.RFQ_ID_GROUP &&
          context.activeTabId == BUYER_DASHBOARD_GRID_TABS.TRADING_ACTIVITY_TAB
        ) {
          let name = '';
          let emitText = '';
          let action = '';
          let actionType = ActionTypes.CREATE;
          let isEnabled = true;
          let tooltip = '';

          switch (params.data.NEGOTIATION_STATUS) {
            case 'IN_PROGRESS': {
              if (params.data.RFQ_OBJECT.PROPOSAL_SENDER == 'SELLER') {
                name = 'View Offer';
              } else {
                name = 'Offer Pending';
              }
              emitText = 'reviewProposal';
              action = 'review-proposal';
              actionType = ActionTypes.REVIEW;
              isEnabled = true;
              tooltip = 'Go to Structured Chat';
              break;
            }
            case 'REQUIRES_CONFIRMATION': {
              name = 'View Pre-Trade Confirm';
              emitText = 'reviewProposal';
              action = 'review-proposal';
              actionType = ActionTypes.REQUIRES_CONFIRMATION;
              isEnabled = true;
              tooltip = 'Go to Structured Chat';
              break;
            }
            case 'CONFIRMED_AWAITING_BUYER': {
              name = 'View Pre-Trade Confirm';
              emitText = 'reviewProposal';
              action = 'review-proposal';
              actionType = ActionTypes.REQUIRES_CONFIRMATION;
              isEnabled = true;
              tooltip = 'Go to Structured Chat';
              break;
            }
            case 'CONFIRMED_AWAITING_SELLER': {
              name = 'Awaiting Confirmation';
              emitText = 'reviewProposal';
              action = 'review-proposal';
              actionType = ActionTypes.REQUIRES_CONFIRMATION;
              isEnabled = true;
              tooltip = 'Go to Structured Chat';
              break;
            }
            case 'REJECTED': {
              name = 'Rejected';
              emitText = 'reviewAndClearRejected';
              action = 'reviewAndClearRejected';
              actionType = ActionTypes.REJECT;
              isEnabled = true;
              break;
            }
            case 'SUCCESSFUL': {
              name = 'Successful';
              emitText = 'reviewProposal';
              action = 'successful';
              actionType = ActionTypes.SUCCESSFUL;
              isEnabled = true;
              tooltip = 'Go to Structured Chat';
              break;
            }
          }

          const reviewBtn = {
            name: name,
            callback: (detail) => {
              context.$emit(
                context.userService
                  .getFilteredPermissions()
                  .includes(PROFILE_RIGHTS.STRUCTURED_CHAT_VIEW)
                  ? emitText
                  : '',
                detail,
              );
            },
            type: actionType,
            actionName: action,
            tooltip: tooltip,
          };

          const actions = [reviewBtn];
          return {
            actions,
            enabled: isEnabled,
          };
        }
      },
    },
  ];
}
